import { inject, injectable } from "@launchtray/tsyringe-async";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { IMyBuildingsController } from "./IMyBuildingsController";
import { IGameModel } from "../../../game/models/IGameModel";
import { SceneManager } from "../../../../../scene/SceneManager";
import { IRenterPolicyModel } from "../../../common/models/Renters/IRenterPolicyModel";
import { UpgradeResult } from "../../../common/models/Building/upgrade/UpgradeResult";
import { FixResult } from "../../../common/models/Building/upgrade/FixResult";
import { IBuildingRankUpModel } from "../../buildingRankUp/model/IBuildingRankUpModel";
import { IBuildingModel } from "../../../common/models/Building/IBuildingModel";
import { IPiggyBankModel } from "../../../common/models/piggybank/IPiggyBankModel";
import { Web3Model } from "../../../../../web3/models/Web3Model";
import { PaymentResult } from "../../../../../web3/PaymentResult";
import { ILogger } from "../../../../logger/ILogger";
import { IContextResolver } from "../../../../composition/IContextResolver";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";
import { MarketplaceModule } from "../../marketplace/MarketplaceModule";
import { CancellationToken } from "async/CancellationToken";
import { BuildingRankUpPopupSceneFactory, BuildingUpgradeItemPopupSceneFactory } from "../../BuildingSceneFactories";
import { ListInMpNotificationPopupScene } from "../../listinmpnotification/ListInMpNotificationPopupScene";
import { IGameViewModel } from "../../../game/models/IGameViewModel";
import { IInventory } from "../../../../../web3/models/IInventory";
import { UserProfileModule } from "../../userprofile/UserProfileModule";
import { IUserLevelUpModel } from "../../userlevelup/model/IUserLevelUpModel";
import { PurchaseHistory } from "../../marketplace/main/models/building/PurchaseHistory";
import { PriceHistoryPopupScene } from "../../pricehistory/PriceHistoryPopupScene";
import { ShowEditPriceSceneParams } from "../../editprice/scene/ShowEditPriceSceneParams";
import { EditPriceModule } from "../../editprice/EditPriceModule";

type PriceHistoryPopupSceneFactory = (priceHistory: Array<PurchaseHistory>) => Promise<PriceHistoryPopupScene>

@injectable()
export class MyBuildingsController implements IMyBuildingsController {
    private readonly sceneManager: SceneManager;
    private readonly playerModel: IPlayerModel;
    private readonly web3Model: Web3Model;
    private readonly inventory: IInventory;
    private readonly gameModel: IGameModel;
    private readonly gameViewModel: IGameViewModel;
    private readonly renterPolicyModel: IRenterPolicyModel;
    private readonly piggyModel: IPiggyBankModel;
    private readonly contextResolver: IContextResolver;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;

    public constructor(
        @inject("IContextResolver") contextResolver: IContextResolver,
        sceneManager: SceneManager,
        web3Model: Web3Model,
        @inject("IInventory") inventory: IInventory,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("IGameModel") gameModel: IGameModel,
        @inject("IGameViewModel") gameViewModel: IGameViewModel,
        @inject("IRenterPolicyModel") renterPolicyModel: IRenterPolicyModel,
        @inject("IPiggyBankModel") piggyModel: IPiggyBankModel,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger
    ) {
        this.sceneManager = sceneManager;
        this.web3Model = web3Model;
        this.inventory = inventory;
        this.playerModel = playerModel;
        this.gameModel = gameModel;
        this.gameViewModel = gameViewModel;
        this.renterPolicyModel = renterPolicyModel;
        this.piggyModel = piggyModel;
        this.contextResolver = contextResolver;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
    }

    public async upgradeClickHandle(building: IBuildingModel, upgradeItemId: string, perCoins: boolean): Promise<UpgradeResult> {
        const upgradeModel = building.upgrades.find(item => item.id.value === upgradeItemId);
        const costInCoins = upgradeModel.upgradeCostInCoins.value;
        const costInTokens = upgradeModel.upgradeCostInTokens.value;
        const result = await this.asyncOperationNotification.autoShowHide(building.upgrade(upgradeItemId, perCoins));
        await this.renterPolicyModel.updateRenterPolicyStatus();
        if (building.userLevelUpModel.value == undefined) {
            this.playerModel.updateValuesFromUpgrade(result.guest);
        }
        if (building.userLevelUpModel.value && building.userLevelUpModel.value.openedPiggyBank) {
            this.piggyModel.updateStatusFromUserLevelUp(building.userLevelUpModel.value.openedPiggyBank);
        }
        perCoins ? this.playerModel.subtractCoins(costInCoins) : this.playerModel.subtractTokens(costInTokens);
        this.gameModel.getCurrentAction();
        return new UpgradeResult(result.upgradeResult);
    }

    public async fixClickHandle(building: IBuildingModel, fixItemId: string, perCoins: boolean): Promise<FixResult> {
        const upgradeModel = building.upgrades.find(item => item.id.value === fixItemId);
        const costInCoins = upgradeModel.fixCostInCoins.value;
        const costInTokens = upgradeModel.fixCostInTokens.value;
        const result = await this.asyncOperationNotification.autoShowHide(building.fix(fixItemId, perCoins));
        if (building.userLevelUpModel.value == undefined) {
            this.playerModel.updateValuesFromUpgrade(result.guest);
        } else {
            this.piggyModel.updateStatusFromUserLevelUp(building.userLevelUpModel.value.openedPiggyBank);
        }
        this.playerModel.checkAndUpdateBuildingHasFixes();
        perCoins ? this.playerModel.subtractCoins(costInCoins) : this.playerModel.subtractTokens(costInTokens);
        this.gameModel.getCurrentAction();
        return new FixResult(result.fixResult);
    }

    public async paymentBuildingWithMint(building: IBuildingModel, cancellationToken: CancellationToken): Promise<PaymentResult> {
        return await this.web3Model.paymentBuildingWithMint(building.id.value, cancellationToken);
    }

    public async showBuildingRankUpPopup(buildingRankUpModel: IBuildingRankUpModel, buildingModel: IBuildingModel, cancellationToken: CancellationToken): Promise<void> {
        try {
            this.asyncOperationNotification.show();
            this.gameViewModel.popupShown();
            const factory: BuildingRankUpPopupSceneFactory = await this.contextResolver.resolve("BuildingRankUpPopupSceneFactory");
            if (cancellationToken.isCancellationRequested) {
                return;
            }
            const scene = await factory(buildingRankUpModel, buildingModel, cancellationToken);
            if (cancellationToken.isCancellationRequested) {
                scene.view?.destroy();
                return;
            }
            this.asyncOperationNotification.hide();
            await this.sceneManager.showViewableAsync(scene);
        }
        catch (e: unknown) {
            this.logger.error("Exception on showing BuildingRankUp: ", e);
        }
        finally {
            this.gameViewModel.popupHidden();
            this.asyncOperationNotification.hide();
        }
    }

    public async showBuildingUpgradeItemPopup(buildingModel: IBuildingModel, cancellationToken: CancellationToken): Promise<void> {
        try {
            this.asyncOperationNotification.show();
            const factory: BuildingUpgradeItemPopupSceneFactory = await this.contextResolver.resolve("BuildingUpgradeItemPopupSceneFactory");
            if (cancellationToken.isCancellationRequested) {
                return;
            }
            const scene = await factory(buildingModel, this.playerModel, cancellationToken);
            if (cancellationToken.isCancellationRequested) {
                scene.view?.destroy();
                return;
            }
            this.asyncOperationNotification.hide();
            await this.sceneManager.showViewableAsync(scene);
        }
        catch (e: unknown) {
            this.logger.error("Exception on showing BuildingUpgrade: ", e);
        }
        finally {
            this.asyncOperationNotification.hide();
        }
    }

    public async listInMarketPlace(buildingId: string, tokenId: string, currentPrice: number, purchaseHistory: Array<PurchaseHistory>, cancellationToken: CancellationToken): Promise<boolean> {
        try {
            this.asyncOperationNotification.show();
            const module = await this.contextResolver.resolve(EditPriceModule);
            if (cancellationToken.isCancellationRequested) {
                return false;
            }
            this.asyncOperationNotification.hide();
            return await module.start(new ShowEditPriceSceneParams(buildingId, tokenId, currentPrice, true, null, purchaseHistory, cancellationToken));
        }
        catch (e: unknown) {
            this.logger.error("Exception on showing EditPrice: ", e);
            return false;
        }
        finally {
            this.asyncOperationNotification.hide();
        }
    }

    public async showUserProfilePopup(userLevelModel?: IUserLevelUpModel): Promise<void> {
        this.gameViewModel.popupShown();
        const userProfileModule = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(UserProfileModule));
        await userProfileModule.start(userLevelModel);
        this.gameViewModel.popupHidden();
    }

    public async updateBuildingsInventory(): Promise<void> {
        try {
            this.asyncOperationNotification.show();
            await this.web3Model.waitSmartWalletConnection();
            await this.inventory.updateBuildings();
        }
        catch (e: unknown) {
            this.logger.error("Exception on updating buildings inventory");
        }
        finally {
            this.asyncOperationNotification.hide();
        }
    }

    public async showListInMpNotificationPopup(): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(ListInMpNotificationPopupScene));
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showMarketPlacePopupScene(buildingArea: number): Promise<void> {
        this.gameViewModel.popupShown();
        const module = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(MarketplaceModule));
        // Important to keep without await cause its scene to scene part
        module.start(buildingArea).finally(() => {
            this.gameViewModel.popupHidden();
        });
    }

    public async loadBuildings(): Promise<void> {
        await this.playerModel.loadBuildings();
    }

    public async showPriceHistoryScene(priceHistory: Array<PurchaseHistory>): Promise<void> {
        try {
            const sceneFactory: PriceHistoryPopupSceneFactory = await this.contextResolver.resolve("PriceHistoryPopupSceneFactory");
            const scene = await sceneFactory(priceHistory);
            return await this.sceneManager.showViewableAsync(scene);
        } catch (e: unknown) {
            this.logger.error("Exception on showing PriceHistory: ", e);
        }
    }

}
